import { useTranslation } from 'react-i18next';
import { Button } from '@parrot-software/design-system';
import { ChevronRight, FileUp, PencilLine } from '@parrot-software/design-system/icons';
import { cn } from '@parrot-software/utils';
import { logger } from '@parrot-software/observability';

const invoiceLog = logger.forDomain('SUB', { subdomain: 'billing' });

interface InvoiceDataMethodStepProps {
  fieldsClassName?: string;
  footerClassName?: string;
  onCancel: () => void;
  onSelectManual: () => void;
  onSelectUpload: () => void;
}

/**
 * First step of the fiscal data creation flow: how the record will be captured.
 *
 * Uploading the SAT proof is the recommended path and takes the whole card; filling the form by
 * hand stays available as a discreet link below it. The choice is delegated to the host, which
 * owns the step state.
 *
 * @param {InvoiceDataMethodStepProps} props - Component properties
 * @returns {React.ReactElement} Method selection step
 */
export const InvoiceDataMethodStep = ({
  fieldsClassName,
  footerClassName,
  onCancel,
  onSelectManual,
  onSelectUpload,
}: InvoiceDataMethodStepProps): React.ReactElement => {
  const { t } = useTranslation('invoiceData');

  const handleSelectUpload = () => {
    invoiceLog.info('selectInvoiceDataMethod', { method: 'upload' });
    onSelectUpload();
  };

  const handleSelectManual = () => {
    invoiceLog.info('selectInvoiceDataMethod', { method: 'manual' });
    onSelectManual();
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col font-sans">
      <div className={cn('flex min-h-0 flex-1 flex-col gap-3', fieldsClassName)}>
        <button
          className="flex w-full items-start gap-3 rounded-lg border border-border bg-background p-4 text-left transition-colors hover:border-primary hover:bg-accent"
          onClick={handleSelectUpload}
          type="button"
        >
          <FileUp className="mt-0.5 h-5 w-5 shrink-0 text-primary" strokeWidth={1.5} />
          <span className="flex flex-1 flex-col gap-0.5">
            <span className="text-sm font-medium text-foreground">{t('invoiceDataForm.methodStep.uploadTitle')}</span>
            <span className="text-xs text-muted-foreground">{t('invoiceDataForm.methodStep.uploadDescription')}</span>
          </span>
          <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        </button>

        <div className="flex">
          <Button
            className="h-auto p-0 text-xs font-normal text-muted-foreground hover:text-primary"
            onClick={handleSelectManual}
            type="button"
            variant="link"
          >
            <PencilLine className="mr-1 h-3 w-3" />
            {t('invoiceDataForm.methodStep.manualTitle')}
          </Button>
        </div>
      </div>

      <div className={cn('mt-4 flex flex-col gap-2', footerClassName)}>
        <Button className="w-full" onClick={onCancel} type="button" variant="outline">
          {t('invoiceDataForm.cancel')}
        </Button>
      </div>
    </div>
  );
};
